import { useState, useRef, useEffect, useCallback } from "react";
import type { MinimalTag } from "@/modules/tag/types";
import { getTagColorStyles } from "@/modules/tag/colors";
import IconClose from "../icons/icon-close";

interface SearchResult {
	tags: MinimalTag[];
	hasMore: boolean;
	nextCursor: string | null;
}

interface TagSelectorProps {
	label?: string;
	description?: string;
	tags: MinimalTag[];
	selectedTagIds: string[];
	onChange: (tagIds: string[]) => void;
	disabled?: boolean;
	placeholder?: string;
	error?: string;
	onCreateTag?: (keyword: string, onCreated: (tagId: string) => void) => void;
	hasMore?: boolean;
	isLoadingMore?: boolean;
	onLoadMore?: () => void;
	onSearch?: (query: string, cursor?: string | null) => Promise<SearchResult | null>;
	onTagSelect?: (tag: MinimalTag) => void;
}

export default function TagSelector({
	label,
	description,
	tags,
	selectedTagIds,
	onChange,
	disabled = false,
	placeholder = "Select tags...",
	error,
	onCreateTag,
	hasMore = false,
	isLoadingMore = false,
	onLoadMore,
	onSearch,
	onTagSelect,
}: TagSelectorProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const [isCreating, setIsCreating] = useState(false);
	const [creatingLabel, setCreatingLabel] = useState("");
	const [isSearching, setIsSearching] = useState(false);
	const [searchResults, setSearchResults] = useState<MinimalTag[] | null>(null);
	const [searchHasMore, setSearchHasMore] = useState(false);
	const [searchNextCursor, setSearchNextCursor] = useState<string | null>(null);
	const [isLoadingMoreSearch, setIsLoadingMoreSearch] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Preserve the order of selectedTagIds when displaying selected tags
	const selectedTags = selectedTagIds.map((id) => tags.find((tag) => tag.id === id)).filter((tag): tag is MinimalTag => tag !== undefined);

	// Use search results when searching, otherwise filter from local tags
	const isSearchMode = onSearch && searchQuery.trim().length > 0;
	const availableTags = isSearchMode && searchResults !== null ? searchResults : tags;
	const filteredTags = availableTags.filter((tag) => !selectedTagIds.includes(tag.id) && (isSearchMode || tag.label.toLowerCase().includes(searchQuery.toLowerCase())));

	// Check if the search query exactly matches an existing tag (case-insensitive)
	const exactMatchExists = availableTags.some((tag) => tag.label.toLowerCase() === searchQuery.toLowerCase().trim());
	const canCreateTag = onCreateTag && searchQuery.trim() && !exactMatchExists;

	// Debounced server-side search
	const performSearch = useCallback(
		async (query: string, cursor?: string | null) => {
			if (!onSearch) return;

			if (cursor) {
				setIsLoadingMoreSearch(true);
			} else {
				setIsSearching(true);
			}

			const result = await onSearch(query, cursor);

			if (result) {
				if (cursor) {
					// Append to existing search results
					setSearchResults((prev) => (prev ? [...prev, ...result.tags] : result.tags));
				} else {
					setSearchResults(result.tags);
				}
				setSearchHasMore(result.hasMore);
				setSearchNextCursor(result.nextCursor);
			}

			setIsSearching(false);
			setIsLoadingMoreSearch(false);
		},
		[onSearch],
	);

	// Reset highlighted index when filtered tags change
	useEffect(() => {
		setHighlightedIndex(0);
	}, [searchQuery]);

	// Debounced search effect
	useEffect(() => {
		if (!onSearch) return;

		// Clear previous timeout
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		const trimmedQuery = searchQuery.trim();

		if (trimmedQuery.length === 0) {
			// Reset search results when query is empty
			setSearchResults(null);
			setSearchHasMore(false);
			setSearchNextCursor(null);
			setIsSearching(false);
			return;
		}

		// Debounce the search
		searchTimeoutRef.current = setTimeout(() => {
			performSearch(trimmedQuery);
		}, 300);

		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, [searchQuery, onSearch, performSearch]);

	// Handle infinite scroll
	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const target = e.target as HTMLDivElement;
		const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

		// Trigger load more when user is within 50px of the bottom
		if (scrollBottom < 50) {
			if (isSearchMode) {
				// Load more search results
				if (searchHasMore && !isLoadingMoreSearch && searchNextCursor) {
					performSearch(searchQuery.trim(), searchNextCursor);
				}
			} else {
				// Load more from main tag list
				if (hasMore && !isLoadingMore && onLoadMore) {
					onLoadMore();
				}
			}
		}
	};

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				setSearchQuery("");
				// Reset search state when closing
				setSearchResults(null);
				setSearchHasMore(false);
				setSearchNextCursor(null);
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleTagSelect = (tagId: string) => {
		// Find the tag in available tags (could be from search results or main tags list)
		const selectedTag = availableTags.find((tag) => tag.id === tagId);
		if (selectedTag && onTagSelect) {
			onTagSelect(selectedTag);
		}
		onChange([...selectedTagIds, tagId]);
		setSearchQuery("");
		// Reset search state
		setSearchResults(null);
		setSearchHasMore(false);
		setSearchNextCursor(null);
		inputRef.current?.focus();
	};

	const handleTagRemove = (tagId: string) => {
		onChange(selectedTagIds.filter((id) => id !== tagId));
	};

	const handleCreateTag = (label: string) => {
		if (!onCreateTag || isCreating) return;
		setIsCreating(true);
		setCreatingLabel(label);
		setSearchQuery("");
		setHighlightedIndex(0);
		// Reset search state
		setSearchResults(null);
		setSearchHasMore(false);
		setSearchNextCursor(null);
		onCreateTag(label, (newTagId) => {
			onChange([...selectedTagIds, newTagId]);
			setIsCreating(false);
			setCreatingLabel("");
			inputRef.current?.focus();
		});
	};

	const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace" && searchQuery === "" && selectedTagIds.length > 0) {
			handleTagRemove(selectedTagIds[selectedTagIds.length - 1]);
		}
		if (e.key === "Escape") {
			setIsOpen(false);
			setSearchQuery("");
			// Reset search state
			setSearchResults(null);
			setSearchHasMore(false);
			setSearchNextCursor(null);
			setHighlightedIndex(0);
		}
		if (e.key === "ArrowDown") {
			e.preventDefault();
			const maxIndex = filteredTags.length + (canCreateTag ? 1 : 0) - 1;
			if (!isOpen) {
				setIsOpen(true);
			} else if (maxIndex >= 0) {
				setHighlightedIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
				// Scroll highlighted item into view
				const listEl = listRef.current;
				if (listEl) {
					const items = listEl.querySelectorAll("button");
					const nextIndex = Math.min(highlightedIndex + 1, maxIndex);
					items[nextIndex]?.scrollIntoView({ block: "nearest" });
				}
			}
		}
		if (e.key === "ArrowUp") {
			e.preventDefault();
			const maxIndex = filteredTags.length + (canCreateTag ? 1 : 0) - 1;
			if (maxIndex >= 0) {
				setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
				// Scroll highlighted item into view
				const listEl = listRef.current;
				if (listEl) {
					const items = listEl.querySelectorAll("button");
					const prevIndex = Math.max(highlightedIndex - 1, 0);
					items[prevIndex]?.scrollIntoView({ block: "nearest" });
				}
			}
		}
		if (e.key === "Enter") {
			e.preventDefault();
			if (isOpen && highlightedIndex < filteredTags.length && filteredTags.length > 0) {
				handleTagSelect(filteredTags[highlightedIndex].id);
				setHighlightedIndex(0);
			} else if (canCreateTag && (highlightedIndex === filteredTags.length || filteredTags.length === 0)) {
				handleCreateTag(searchQuery.trim());
			}
		}
	};

	return (
		<div className={disabled ? "opacity-50" : ""}>
			{label && <label className="block text-sm font-medium text-primary mb-1.5">{label}</label>}
			<div ref={containerRef} className="relative">
				<div
					className={`min-h-[42px] w-full border rounded-lg px-2 py-1.5 flex flex-wrap items-center gap-1.5 cursor-text bg-white ${
						isOpen ? "ring-2 ring-brand ring-offset-1 border-transparent" : error ? "border-red-500" : "border-gray-300"
					} ${disabled ? "cursor-not-allowed" : ""}`}
					onClick={() => {
						if (!disabled) {
							setIsOpen(true);
							inputRef.current?.focus();
						}
					}}
				>
					{selectedTags.map((tag) => {
						const colorStyles = getTagColorStyles(tag.color);
						return (
							<span key={tag.id} className={`inline-flex items-center gap-1.5 px-2 py-0.5 ${colorStyles.bg} ${colorStyles.text} text-sm rounded-md`}>
								{tag.label}
								{!disabled && (
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											handleTagRemove(tag.id);
										}}
										className={`hover:opacity-80 cursor-pointer ${colorStyles.text}`}
									>
										<IconClose className="w-3 h-3" />
									</button>
								)}
							</span>
						);
					})}
					{isCreating && (
						<span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-200 text-gray-600 text-sm rounded-md animate-pulse">
							{creatingLabel}
							<svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
								<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
							</svg>
						</span>
					)}
					<input
						ref={inputRef}
						type="text"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						onFocus={() => setIsOpen(true)}
						onKeyDown={handleInputKeyDown}
						placeholder={selectedTags.length === 0 ? placeholder : ""}
						disabled={disabled}
						className="flex-1 min-w-[120px] outline-none text-sm placeholder:text-gray-400 bg-transparent disabled:cursor-not-allowed"
					/>
				</div>

				{isOpen && (filteredTags.length > 0 || canCreateTag || isLoadingMore || isLoadingMoreSearch || isSearching) && !isCreating && (
					<div ref={listRef} className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto" onScroll={handleScroll}>
						{isSearching && filteredTags.length === 0 ? (
							<div className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-tertiary">
								<svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
									<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									/>
								</svg>
								Searching...
							</div>
						) : (
							<>
								{filteredTags.map((tag, index) => {
									const colorStyles = getTagColorStyles(tag.color);
									return (
										<button
											key={tag.id}
											type="button"
											onClick={() => handleTagSelect(tag.id)}
											onMouseEnter={() => setHighlightedIndex(index)}
											className={`w-full px-3 py-2 text-left text-sm cursor-pointer flex items-center gap-2 ${index === highlightedIndex ? "bg-gray-100" : "hover:bg-gray-50"}`}
										>
											<span className={`w-2 h-2 rounded-full shrink-0 ${colorStyles.bg}`} />
											<span className="text-primary whitespace-nowrap">{tag.label}</span>
											{tag.description && <span className="text-xs text-tertiary truncate">{tag.description}</span>}
										</button>
									);
								})}
								{canCreateTag && (
									<button
										type="button"
										onClick={() => handleCreateTag(searchQuery.trim())}
										onMouseEnter={() => setHighlightedIndex(filteredTags.length)}
										className={`w-full px-3 py-2 text-left text-sm cursor-pointer flex items-center gap-2 text-brand font-medium ${filteredTags.length > 0 ? "border-t border-gray-100" : ""} ${filteredTags.length === highlightedIndex ? "bg-gray-100" : "hover:bg-gray-50"}`}
									>
										<span className="w-2 h-2 flex items-center justify-center">+</span>
										<span>Create "{searchQuery.trim()}"</span>
									</button>
								)}
								{(isLoadingMore || isLoadingMoreSearch) && (
									<div className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-tertiary">
										<svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
											/>
										</svg>
										Loading...
									</div>
								)}
							</>
						)}
					</div>
				)}

				{isOpen && filteredTags.length === 0 && searchQuery && !canCreateTag && !isSearching && (
					<div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3">
						<p className="text-sm text-tertiary text-center">Tag already exists</p>
					</div>
				)}
			</div>
			{description && <p className="text-xs text-tertiary mt-1.5">{description}</p>}
			{error && <p className="text-sm text-red-500 mt-1">{error}</p>}
		</div>
	);
}
